import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import { Checkbox, CheckboxProps } from "../Checkbox";
import { useMergeRefs, useTrackingId } from "../../hooks";

import cx from "classnames";
import styles from "./ListView.module.scss";
import { CheckboxState } from "../Checkbox/types";
import { useListView } from "./internal/ListViewContext";
import { DataTrackingId } from "../../types";

type ItemType = {
  label: string;
  disabled?: boolean;
  [key: string]: unknown;
};

/**
 * Props for the ListViewOption component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ListViewOptionProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onChange"
> & {
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Callback when the option changes */
  onChange?: (e: ChangeEvent<HTMLInputElement>, state?: CheckboxState) => void;
} & (
    | {
        /** The item object when using items prop */
        item: ItemType;
      }
    | {
        /** The display label for the option */
        label: string;
      }
  ) &
  DataTrackingId;

/**
 * ListViewOption component for individual selectable items within a listview.
 *
 * Features:
 * - Displays selectable options with proper ARIA attributes
 * - Supports disabled state for non-selectable options
 * - Keyboard navigation support
 * - Accessible with screen reader support
 *
 * @example
 * <ListView.Option item={{ label: "Option 1" }} />
 */
export const ListViewOption = forwardRef<HTMLDivElement, ListViewOptionProps>(
  (props, ref) => {
    const {
      className,
      children,
      onChange,
      style,
      disabled: disabledProp,
      ...remainingProps
    } = props;

    const optionRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergeRefs([ref, optionRef]);

    const cellRef = useRef<HTMLDivElement>(null);
    const rowId = useId();
    const {
      selectedItems,
      setSelectedItems,
      indeterminateItems,
      controlled,
      onSelectionChange,
    } = useListView();
    const disabled =
      "item" in remainingProps
        ? disabledProp || remainingProps.item.disabled
        : disabledProp;

    useEffect(() => {
      const focusables = optionRef.current?.querySelectorAll(
        "button:not(:disabled):not([aria-disabled='true']), input:not(:disabled):not([aria-disabled='true']), [tabindex='0']:not(:disabled):not([aria-disabled='true'])",
      );
      focusables?.forEach((item, i) => {
        item.setAttribute("tabindex", "-1");
        if (item.hasAttribute("id")) return;
        item.setAttribute("id", `${rowId}-${i}`);
      });
    }, [rowId]);

    const label =
      "item" in remainingProps
        ? remainingProps.item.label
        : remainingProps.label;

    const isSelected = useMemo(
      () => matchInArray(label, selectedItems),
      [selectedItems, label],
    );

    const isIndeterminate = useMemo(
      () => matchInArray(label, indeterminateItems),
      [indeterminateItems, label],
    );

    const onChangeHandler: CheckboxProps["onChange"] = (e) => {
      onChange?.(e, { checked: isSelected });
    };

    const onClickHandler: CheckboxProps["onClick"] = () => {
      updateSelectedItems();
    };

    const removeItemFromSelectedItems = (itemLabelToRemove: string) => {
      return (selectedItems as (ItemType | string)[]).filter(
        (potentialRemove) => {
          if (typeof potentialRemove === "string") {
            return potentialRemove !== itemLabelToRemove;
          }
          return potentialRemove.label !== itemLabelToRemove;
        },
      );
    };

    const addItemFromSelectedItems = (itemToAdd: ItemType | string) => {
      return [...selectedItems, itemToAdd];
    };

    function updateSelectedItems() {
      const isPartOfSelectedItems = matchInArray(label, selectedItems);
      const callback = controlled ? onSelectionChange : setSelectedItems;
      if (isPartOfSelectedItems) {
        callback?.(removeItemFromSelectedItems(label));
      } else {
        callback?.(
          addItemFromSelectedItems(
            "item" in remainingProps
              ? remainingProps.item
              : remainingProps.label,
          ),
        );
      }
    }

    const data = {
      label: "item" in props ? props.item?.label : props.label,
    };

    const trackingId = useTrackingId({
      name: "ListViewOption",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const {
      item: _item,
      label: _label,
      ...forwardedProps
    } = remainingProps as {
      // This is to satisfy the type checker and allow us to destructure the item and label props which "could" exist.
      // We're destructuring them because we don't want to forward them to the div element.
      item: ItemType;
      label: string;
    };

    return (
      <div
        data-tracking-id={trackingId}
        className={cx([styles["listview-option"]], className)}
        data-anv="listview-option"
        style={style}
        ref={mergedRef}
        aria-disabled={disabled}
        aria-selected={isSelected}
        aria-label={label ?? "Select"}
        role="row"
        {...forwardedProps}
      >
        <div className={styles["cell"]} ref={cellRef} role="gridcell">
          <Checkbox
            disabled={disabled}
            checked={isIndeterminate ? undefined : isSelected}
            indeterminate={isIndeterminate}
            aria-label={label ?? "Select"}
            onChange={onChangeHandler}
            onClick={onClickHandler}
          />
        </div>
        {children}
      </div>
    );
  },
);

ListViewOption.displayName = "ListViewOption";

/**
 * Helper function to check if a list view option item is in an array (e.g. selected items or indeterminate items)
 * @param itemLabel - The label of the item to check if it is in the array
 * @param array - The array to check if the item is in
 * @returns True if the item is in the array, false otherwise
 */
function matchInArray(
  itemLabel: string,
  array: (ItemType | unknown)[],
): boolean {
  return array.some(
    (i) => itemLabel === i || itemLabel === (i as ItemType).label,
  );
}
